import { CheckboxField, NumberField, Typography } from '@iheartjane/reefer';

import type {
  ConditionMultiselectRangeValue,
  ConditionRangeItem,
  ConditionOption,
} from './conditions.types';
import { optionKey } from './util/conditionSelection.util';
import { toBound } from './util/toBound';
import { toBoundLabel } from './util/toBoundLabel';

import styles from './multiselectRangeConditions.module.css';

/** Field names double as DOM ids, so they have to be row-unique */
const toFieldName = (
  itemValue: string,
  suffix: 'checkbox' | 'error' | 'maximum' | 'minimum'
): string => `multiselect-range-${itemValue}-${suffix}`;

/** A bound the section leaves open has nothing to suggest */
const toPlaceholder = (bound?: number): string | undefined =>
  bound === undefined ? undefined : String(bound);

const omitItem = (
  value: ConditionMultiselectRangeValue,
  itemValue: string
): ConditionMultiselectRangeValue => {
  const remaining = { ...value };

  delete remaining[itemValue];

  return remaining;
};

export interface MultiselectRangeConditionsProps {
  /** Decimal places the inputs accept, ex 2 for dollars and cents */
  allowedDecimalPlaces?: number;

  /** Unit rendered to the right of the inputs, ex "%" */
  endUnit?: string;

  /**
   * Validation message per item, keyed like `value`. The caller owns the rule —
   * `validateRange` computes the message this expects.
   */
  errors?: Record<string, string>;

  /** The items a row is drawn for, in the order they should appear */
  items: ConditionOption[];

  /** Fired with every row, so untouched rows carry over unchanged */
  onChange(value: ConditionMultiselectRangeValue): void;

  /**
   * Bounds and column labels shared by every row. Each part is optional: a
   * missing bound just goes unsuggested, and a missing label leaves the columns
   * headed "Minimum" and "Maximum".
   */
  range?: Partial<ConditionRangeItem>;

  /** Unit rendered to the left of the inputs, ex "$" */
  startUnit?: string;

  /** Legal increment for the inputs, ex 0.01 for dollars and cents */
  step?: number;

  /**
   * The selected items and their ranges, keyed by `optionKey` — an item's own
   * value when it is a string, else its unit rule flattened, ex `3.5g`. An item
   * missing from the map is unchecked; present with an open range is checked but
   * unbounded.
   *
   * NB: Reefer's `NumberField` ignores an `undefined` `value`, so clearing a
   * bound from out here will not empty the input. Callers that need a hard
   * reset have to remount.
   */
  value: ConditionMultiselectRangeValue;
}

/**
 * One row per item, each with a lower and upper bound. Checking an item selects
 * it at any value; the bounds narrow it from there. Weight and price is the
 * first of these — a row per bulk weight, each with a price floor and ceiling.
 *
 * Presentational: `range` describes what is allowed but is not enforced here,
 * and validation messages are passed in rather than derived.
 *
 * Laid out as a grid rather than a table so an invalid row's message can span
 * both bound columns. The bound columns are therefore labeled once, in a heading
 * row, and each input hides its own label rather than dropping it — a row still
 * needs an accessible name to tell "1g maximum" apart from "3.5g maximum".
 */
export const MultiselectRangeConditions = ({
  allowedDecimalPlaces,
  endUnit,
  errors,
  items,
  onChange,
  range,
  startUnit,
  step,
  value,
}: MultiselectRangeConditionsProps) => {
  const sharedFieldProps = {
    allowedDecimalPlaces,
    disableOnWheel: true,
    endUnit,
    startUnit,
    step,
    width: '100%',
  };

  const toggleItem = (itemValue: string, isSelected: boolean) =>
    onChange(
      isSelected ? { ...value, [itemValue]: {} } : omitItem(value, itemValue)
    );

  const updateBound = (
    itemValue: string,
    bound: 'max' | 'min',
    fieldValue?: number | string
  ) =>
    onChange({
      ...value,
      [itemValue]: { ...value[itemValue], [bound]: toBound(fieldValue) },
    });

  return (
    <div className={styles['multiselectRangeConditions']}>
      <div className={styles['row']}>
        {/* The item column carries no heading — its checkboxes name themselves */}
        <span />
        <Typography variant="body-semibold">
          {toBoundLabel('Minimum', range?.label)}
        </Typography>
        <Typography variant="body-semibold">
          {toBoundLabel('Maximum', range?.label)}
        </Typography>
      </div>
      {items.map((item) => {
        /** An item's value may be a unit rule, so the row keys off its handle */
        const itemKey = optionKey(item);
        const selectedRange = value[itemKey];
        const isSelected = selectedRange !== undefined;
        const error = errors?.[itemKey];

        /** Both inputs read as invalid — the rule is about the pair, not a bound */
        const invalidFieldProps = error
          ? {
              'aria-describedby': toFieldName(itemKey, 'error'),
              'aria-invalid': true,
              className: styles['invalidField'],
            }
          : {};

        return (
          <div className={styles['row']} key={itemKey}>
            <CheckboxField
              checked={isSelected}
              label={item.label}
              name={toFieldName(itemKey, 'checkbox')}
              onChange={(checked) => toggleItem(itemKey, checked)}
            />
            <NumberField
              {...sharedFieldProps}
              {...invalidFieldProps}
              disabled={!isSelected}
              label={`${item.label} minimum`}
              labelHidden
              name={toFieldName(itemKey, 'minimum')}
              placeholder={toPlaceholder(range?.min)}
              value={selectedRange?.min}
              onChange={(min) => updateBound(itemKey, 'min', min)}
            />
            <NumberField
              {...sharedFieldProps}
              {...invalidFieldProps}
              disabled={!isSelected}
              label={`${item.label} maximum`}
              labelHidden
              name={toFieldName(itemKey, 'maximum')}
              placeholder={toPlaceholder(range?.max)}
              value={selectedRange?.max}
              onChange={(max) => updateBound(itemKey, 'max', max)}
            />
            {error && (
              <Typography
                aria-live="polite"
                className={styles['error']}
                color="error"
                id={toFieldName(itemKey, 'error')}
              >
                {error}
              </Typography>
            )}
          </div>
        );
      })}
    </div>
  );
};
